import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ProductForm from "@/components/products/ProductForm";
import {
  useAdminStores,
  useCreateAdminProductMutation,
  useUpdateAdminProductMutation,
} from "@/hooks/queries";
import { useQuery } from "@tanstack/react-query";
import { productsService } from "@/services/productsService";
import { formDataToProductCreateDto, formDataToProductUpdateDto, productResponseToFormData } from "@/lib/mappers/store-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import type { ProductFormData } from "@/types/product";

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdit = Boolean(id);
  const productId = id ? Number(id) : 0;

  const { data: storesData, isLoading: storesLoading } = useAdminStores(1, 200);
  const stores = useMemo(
    () => (storesData?.items ?? []).filter((s) => s.status === "approved"),
    [storesData?.items],
  );

  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ["admin", "product", productId],
    queryFn: () => productsService.getById(productId),
    enabled: isEdit && productId > 0,
  });

  const createMutation = useCreateAdminProductMutation();
  const updateMutation = useUpdateAdminProductMutation();

  const [storeId, setStoreId] = useState<string>("");
  const [publish, setPublish] = useState(true);

  const isLoading = storesLoading || (isEdit && productLoading);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (isEdit && !product) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Product not found.</p>
        <Button variant="outline" asChild>
          <Link to="/admin/products">Back to products</Link>
        </Button>
      </div>
    );
  }

  const handleSubmit = async (data: ProductFormData) => {
    try {
      if (isEdit && product) {
        const dto = formDataToProductUpdateDto(data, {
          includeAdminPricing: true,
          isActive: product.isActive,
        });
        await updateMutation.mutateAsync({ id: product.id, dto });
        toast({ title: "Product updated", description: "Full pricing and details saved." });
        navigate(`/admin/products/${product.id}`);
        return;
      }

      if (!storeId) {
        toast({ title: "Select a store", variant: "destructive" });
        return;
      }

      const created = formDataToProductCreateDto(data);
      await createMutation.mutateAsync({
        ...created,
        storeId: Number(storeId),
        publish,
      });
      toast({
        title: "Product created",
        description: publish ? "Approved and published for the store." : "Approved (unpublished).",
      });
      navigate("/admin/products");
    } catch (error) {
      toast({
        title: isEdit ? "Failed to update product" : "Failed to create product",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit && product ? `/admin/products/${product.id}` : "/admin/products")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">
            {isEdit ? "Edit product" : "Add product"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Admin pricing: MRP, DP, GST, WP, RP, CP, DC, BV
            {isEdit && product ? ` · ${product.storeName}` : ""}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Store & publish</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Store *</Label>
            {isEdit ? (
              <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium">
                {product?.storeName} (#{product?.storeId})
              </p>
            ) : (
              <Select value={storeId} onValueChange={setStoreId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-muted-foreground">
              Product BV is deducted from the store&apos;s BV balance. Credit BV first if needed.
            </p>
          </div>

          {!isEdit && (
            <div className="flex items-center justify-between rounded-lg border p-3 sm:mt-6">
              <div>
                <Label htmlFor="publish">Publish immediately</Label>
                <p className="text-xs text-muted-foreground">Approved and visible on storefront</p>
              </div>
              <Switch id="publish" checked={publish} onCheckedChange={setPublish} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Product details & pricing</CardTitle>
        </CardHeader>
        <CardContent>
          <ProductForm
            defaultValues={product ? productResponseToFormData(product) : undefined}
            onSubmit={handleSubmit}
            onCancel={() =>
              navigate(isEdit && product ? `/admin/products/${product.id}` : "/admin/products")
            }
            submitLabel={isEdit ? "Save product" : "Create product"}
            pricePanel="admin"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminProductForm;
